import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { ArrowLeft, Edit2, User, Calendar, Ruler, Scale, Circle, Activity, TrendingUp, Clock, AlertCircle, ChevronRight } from 'lucide-react'
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, ReferenceLine } from 'recharts'
import DashboardLayout from '../components/DashboardLayout'
import PlayerForm from '../components/PlayerForm'
import playerService from '../services/playerService'
import { T, globalStyles } from '../theme'
import { useSeason, formatSeasonShort } from '../context/SeasonContext'
import { POSITION_COLORS } from '../constants/positionColors'
import { resultLetter, scoreDisplay } from '../utils/matchResult'

// ── Helpers réseau pour fetch direct (saison forcée) ──────────
const API = import.meta.env.VITE_API_URL || 'https://backend-pued.onrender.com/api'
function authHeaders() {
  const token = localStorage.getItem('insightball_token')
  return token ? { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' } : { 'Content-Type': 'application/json' }
}

const G = {
  bg: T.bg, card: T.surface, border: T.rule,
  text: T.ink, muted: T.muted, gold: T.gold, goldD: T.goldD,
  goldBg: T.goldBg, goldBdr: T.goldBdr,
  mono: T.mono, display: T.display,
  dark: '#0a0908', dark2: '#0f0e0c',
  green: '#22c55e', red: '#ef4444', blue: '#3b82f6', orange: '#f59e0b', yellow: '#eab308',
}


const FOOT = {
  'droit':      { label: 'Pied droit',   short: 'D', color: G.gold },
  'gauche':     { label: 'Pied gauche',  short: 'G', color: G.blue },
  'ambidextre': { label: 'Ambidextre',   short: 'A', color: G.green },
}

const STATUS = {
  'actif':    { label: 'Actif',    color: G.green },
  'blessé':   { label: 'Blessé',   color: G.red },
  'suspendu': { label: 'Suspendu', color: G.orange },
  'inactif':  { label: 'Inactif',  color: 'rgba(26,25,22,0.35)' },
}


// Métriques par poste pour les courbes de progression
const POSITION_METRICS = {
  'Gardien': [
    { key: 'saves', label: 'Arrêts', color: G.yellow, unit: '' },
    { key: 'minutes', label: 'Minutes', color: G.blue, unit: "'" },
    { key: 'rating', label: 'Note', color: G.gold, unit: '/10' },
  ],
  'Défenseur': [
    { key: 'duels_won_pct', label: 'Duels gagnés', color: G.blue, unit: '%' },
    { key: 'interceptions', label: 'Interceptions', color: G.green, unit: '' },
    { key: 'distance_km', label: 'Distance', color: G.gold, unit: 'km' },
  ],
  'Milieu': [
    { key: 'pass_success', label: 'Passes réussies', color: G.green, unit: '%' },
    { key: 'key_passes', label: 'Passes clés', color: G.gold, unit: '' },
    { key: 'distance_km', label: 'Distance', color: G.blue, unit: 'km' },
  ],
  'Attaquant': [
    { key: 'goals', label: 'Buts', color: G.red, unit: '' },
    { key: 'assists', label: 'Passes D.', color: G.blue, unit: '' },
    // PROMESSES-ANALYSE-001 · « Tirs cadrés » RETIRE · cet onglet ne pouvait
    // rien afficher. `shots_on_target` ne vient que du chemin pipeline
    // (`match.player_stats`), et `player_stats` est NUL sur les 70 matchs de
    // la base · la statistique individuelle n'est jamais produite. Le chemin
    // de repli (routes/players.py) le pose a 0 en dur.
    //
    // ⚠️ Les autres postes portent le meme defaut, a plus grande echelle ·
    // Defenseur et Milieu n'ont AUCUNE metrique reelle, Gardien une sur trois.
    // Non traite ici · leur retrait laisserait ces postes sans aucun onglet,
    // ce qui est une decision produit. Voir P1-METRIQUES-JOUEUR-VIDES-001.
  ],
}

function getMetricsForPosition(position) {
  return POSITION_METRICS[position] || POSITION_METRICS['Milieu']
}

function getAge(birthDate) {
  if (!birthDate) return null
  const today = new Date()
  const birth = new Date(birthDate)
  let age = today.getFullYear() - birth.getFullYear()
  const m = today.getMonth() - birth.getMonth()
  if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) age--
  return age
}

function formatDate(d) {
  if (!d) return '—'
  const date = new Date(d)
  return date.toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })
}

// Génère un résumé textuel automatique basé sur les données de progression
function generateProgressSummary(history, metrics, position) {
  if (!history || history.length < 3) return null

  const summaries = []
  const recent = history.slice(0, 5)
  const older = history.slice(5)

  metrics.forEach(({ key, label, unit }) => {
    const recentVals = recent.map(m => m[key]).filter(v => v != null && v > 0)
    const olderVals = older.map(m => m[key]).filter(v => v != null && v > 0)

    if (recentVals.length < 2) return

    const recentAvg = recentVals.reduce((a, b) => a + b, 0) / recentVals.length
    const olderAvg = olderVals.length > 0
      ? olderVals.reduce((a, b) => a + b, 0) / olderVals.length
      : null

    if (olderAvg !== null && olderAvg > 0) {
      const delta = ((recentAvg - olderAvg) / olderAvg) * 100
      if (delta >= 10) {
        summaries.push({ type: 'up', text: `En progression sur ${label.toLowerCase()} (+${Math.round(delta)}% sur les 5 derniers matchs).` })
      } else if (delta <= -10) {
        summaries.push({ type: 'down', text: `Baisse observée sur ${label.toLowerCase()} (−${Math.round(Math.abs(delta))}% sur les 5 derniers matchs).` })
      }
    }

    // Constance
    if (recentVals.length >= 4) {
      const max = Math.max(...recentVals)
      const min = Math.min(...recentVals)
      const variance = max - min
      if (variance <= recentAvg * 0.15) {
        summaries.push({ type: 'stable', text: `Régularité notable sur ${label.toLowerCase()} — performances stables.` })
      }
    }
  })

  return summaries.slice(0, 2)
}

/* ── Tooltip custom pour Recharts ── */
function CustomTooltip({ active, payload, label }) {
  if (!active || !payload || !payload.length) return null
  return (
    <div style={{
      background: G.dark2, border: `1px solid rgba(79,142,247,0.2)`,
      padding: '10px 14px', fontFamily: G.mono,
    }}>
      <div style={{ fontSize: 8, letterSpacing: '.12em', color: G.muted, marginBottom: 6, textTransform: 'uppercase' }}>{label}</div>
      {payload.map((p, i) => (
        <div key={i} style={{ fontSize: 11, color: p.color, marginBottom: 2 }}>
          {p.name} : <strong style={{ color: '#f5f2eb' }}>{p.value != null ? p.value : '—'}{p.unit || ''}</strong>
        </div>
      ))}
    </div>
  )
}

/* ── Stat mini-card ── */
function StatBox({ value, label, sub, color, big, dark }) {
  return (
    <div style={{
      background: dark ? '#111009' : '#fff',
      border: dark ? '1px solid rgba(245,242,235,0.08)' : '1px solid rgba(26,25,22,0.09)',
      borderTop: `2px solid ${color || 'rgba(79,142,247,0.28)'}`,
      padding: big ? '20px 18px' : '16px 14px',
      display: 'flex', flexDirection: 'column', gap: 2,
    }}>
      <span style={{ fontFamily: G.display, fontSize: big ? 38 : 28, lineHeight: 1, color: dark ? '#f5f2eb' : G.text, letterSpacing: '.01em' }}>
        {value ?? '—'}
      </span>
      <span style={{ fontFamily: G.mono, fontSize: 9, letterSpacing: '.12em', textTransform: 'uppercase', color: dark ? 'rgba(245,242,235,0.35)' : G.muted }}>
        {label}
      </span>
      {sub && <span style={{ fontFamily: G.mono, fontSize: 8, color: dark ? 'rgba(245,242,235,0.25)' : 'rgba(26,25,22,0.30)', marginTop: 2 }}>{sub}</span>}
    </div>
  )
}

/* ── Bar chart simple ── */
function ProgressBar({ value, max, color, label, dark }) {
  const pct = max > 0 ? Math.round((value / max) * 100) : 0
  return (
    <div style={{ marginBottom: 12 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
        <span style={{ fontFamily: G.mono, fontSize: 9, letterSpacing: '.08em', textTransform: 'uppercase', color: dark ? 'rgba(245,242,235,0.5)' : G.muted }}>{label}</span>
        <span style={{ fontFamily: G.mono, fontSize: 9, fontWeight: 600, color: dark ? '#f5f2eb' : G.text }}>{value}</span>
      </div>
      <div style={{ height: 4, background: dark ? 'rgba(255,255,255,0.07)' : G.border, overflow: 'hidden' }}>
        <div style={{ height: '100%', width: `${pct}%`, background: color || G.gold, transition: 'width .8s ease' }} />
      </div>
    </div>
  )
}

/* ── Onglet Progression ── */
function ProgressionTab({ stats, player, isMobile }) {
  const [range, setRange] = useState('all')
  const [activeMetric, setActiveMetric] = useState(0)

  const allHistory = (stats?.all?.match_history || [])
    .slice()
    .sort((a, b) => new Date(a.date) - new Date(b.date))

  const filteredHistory = range === '5' ? allHistory.slice(-5)
    : range === '10' ? allHistory.slice(-10)
    : allHistory

  const metrics = getMetricsForPosition(player.position)
  const summary = generateProgressSummary([...allHistory].reverse(), metrics, player.position)

  // Prépare les données pour Recharts
  const chartData = filteredHistory.map(m => {
    const label = m.opponent
      ? (m.date ? `${new Date(m.date).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })} · ${m.opponent}` : m.opponent)
      : m.date ? new Date(m.date).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' }) : '?'
    const point = { label }
    metrics.forEach(({ key }) => {
      point[key] = m[key] != null ? m[key] : null
    })
    return point
  })

  if (allHistory.length === 0) {
    return (
      <div style={{ textAlign: 'center', padding: '64px 24px', background: G.card, border: `1px solid ${G.border}` }}>
        <TrendingUp size={32} color='rgba(26,25,22,0.12)' style={{ marginBottom: 14 }} />
        <h3 style={{ fontFamily: G.display, fontSize: 22, textTransform: 'uppercase', color: G.muted, marginBottom: 8, letterSpacing: '.02em' }}>
          Progression à venir
        </h3>
        <p style={{ fontFamily: G.mono, fontSize: 10, letterSpacing: '.06em', color: 'rgba(26,25,22,0.30)', lineHeight: 1.7, maxWidth: 340, margin: '0 auto' }}>
          Les courbes d'évolution apparaîtront après les premiers matchs analysés.
        </p>
      </div>
    )
  }

  if (allHistory.length < 3) {
    return (
      <div style={{ textAlign: 'center', padding: '48px 24px', background: G.card, border: `1px solid ${G.border}` }}>
        <TrendingUp size={28} color={G.gold} style={{ marginBottom: 12 }} />
        <p style={{ fontFamily: G.mono, fontSize: 10, letterSpacing: '.06em', color: G.muted, lineHeight: 1.7 }}>
          {allHistory.length} match{allHistory.length > 1 ? 's' : ''} analysé{allHistory.length > 1 ? 's' : ''}.<br />
          Les courbes de progression s'afficheront à partir de 3 matchs.
        </p>
      </div>
    )
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

      {/* En-tête + sélecteur de période */}
      <div style={{
        background: G.card, border: `1px solid ${G.border}`,
        borderTop: `2px solid ${G.gold}`,
        padding: '20px 24px',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12, marginBottom: 20 }}>
          <div style={{ fontFamily: G.mono, fontSize: 9, letterSpacing: '.16em', textTransform: 'uppercase', color: G.gold, display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ width: 16, height: 1, background: G.gold }} />Courbes de progression — {player.position}
          </div>
          {/* Sélecteur période */}
          <div style={{ display: 'flex', gap: 0, border: `1px solid ${G.border}` }}>
            {[
              { id: 'all', label: 'Saison' },
              { id: '10', label: '10 matchs' },
              { id: '5', label: '5 matchs' },
            ].map(opt => (
              <button key={opt.id} onClick={() => setRange(opt.id)} style={{
                padding: '6px 14px', fontFamily: G.mono, fontSize: 8, letterSpacing: '.1em', textTransform: 'uppercase',
                background: range === opt.id ? G.gold : 'transparent',
                color: range === opt.id ? '#0f0f0d' : G.muted,
                border: 'none', borderRight: opt.id !== '5' ? `1px solid ${G.border}` : 'none',
                cursor: 'pointer', transition: 'all .15s',
              }}>
                {opt.label}
              </button>
            ))}
          </div>
        </div>

        {/* Sélecteur de métrique (tabs) */}
        <div style={{ display: 'flex', gap: 0, marginBottom: 20, borderBottom: `1px solid ${G.border}` }}>
          {metrics.map((m, i) => (
            <button key={m.key} onClick={() => setActiveMetric(i)} style={{
              padding: '8px 16px', fontFamily: G.mono, fontSize: 8, letterSpacing: '.1em', textTransform: 'uppercase',
              background: 'transparent', border: 'none',
              borderBottom: activeMetric === i ? `2px solid ${m.color}` : '2px solid transparent',
              color: activeMetric === i ? m.color : G.muted,
              cursor: 'pointer', transition: 'all .15s',
            }}>
              {m.label}
            </button>
          ))}
          {/* Toutes les métriques */}
          <button onClick={() => setActiveMetric(-1)} style={{
            padding: '8px 16px', fontFamily: G.mono, fontSize: 8, letterSpacing: '.1em', textTransform: 'uppercase',
            background: 'transparent', border: 'none',
            borderBottom: activeMetric === -1 ? `2px solid ${G.muted}` : '2px solid transparent',
            color: activeMetric === -1 ? G.text : G.muted,
            cursor: 'pointer', transition: 'all .15s',
          }}>
            Tout
          </button>
        </div>

        {/* Graphe Recharts */}
        <div style={{ height: isMobile ? 200 : 260 }}>
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={chartData} margin={{ top: 4, right: 8, bottom: 0, left: -20 }}>
              <XAxis
                dataKey="label"
                tick={{ fontFamily: G.mono, fontSize: 7, fill: 'rgba(26,25,22,0.35)', letterSpacing: '.06em' }}
                tickLine={false}
                axisLine={{ stroke: G.border }}
                interval={isMobile ? 'preserveStartEnd' : 0}
                angle={isMobile ? -30 : -20}
                textAnchor="end"
                height={isMobile ? 40 : 36}
              />
              <YAxis
                tick={{ fontFamily: G.mono, fontSize: 7, fill: 'rgba(26,25,22,0.35)' }}
                tickLine={false}
                axisLine={false}
                width={32}
              />
              <Tooltip content={<CustomTooltip />} />
              {(activeMetric === -1 ? metrics : [metrics[activeMetric]]).map((m, i) => (
                <Line
                  key={m.key}
                  type="monotone"
                  dataKey={m.key}
                  name={m.label}
                  stroke={m.color}
                  strokeWidth={activeMetric === -1 ? 1.5 : 2.5}
                  dot={{ fill: m.color, r: activeMetric === -1 ? 2 : 3, strokeWidth: 0 }}
                  activeDot={{ r: 5, fill: m.color }}
                  connectNulls={false}
                  unit={m.unit}
                />
              ))}
            </LineChart>
          </ResponsiveContainer>
        </div>

        {/* Légende */}
        <div style={{ display: 'flex', gap: 16, marginTop: 12, flexWrap: 'wrap' }}>
          {metrics.map((m, i) => (
            <div key={m.key} style={{
              display: 'flex', alignItems: 'center', gap: 6,
              opacity: activeMetric === -1 || activeMetric === i ? 1 : 0.3,
              cursor: 'pointer', transition: 'opacity .15s',
            }} onClick={() => setActiveMetric(activeMetric === i ? -1 : i)}>
              <div style={{ width: 20, height: 2, background: m.color }} />
              <span style={{ fontFamily: G.mono, fontSize: 8, letterSpacing: '.08em', color: G.muted, textTransform: 'uppercase' }}>
                {m.label}{m.unit ? ` (${m.unit})` : ''}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* Résumé textuel automatique */}
      {summary && summary.length > 0 && (
        <div style={{ background: G.card, border: `1px solid ${G.border}`, padding: '20px 24px' }}>
          <div style={{ fontFamily: G.mono, fontSize: 9, letterSpacing: '.16em', textTransform: 'uppercase', color: G.gold, marginBottom: 14, display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ width: 16, height: 1, background: G.gold }} />Analyse automatique
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {summary.map((s, i) => (
              <div key={i} style={{
                display: 'flex', alignItems: 'flex-start', gap: 12,
                padding: '12px 16px',
                background: s.type === 'up' ? `${G.green}08` : s.type === 'down' ? `${G.red}08` : G.goldBg,
                border: `1px solid ${s.type === 'up' ? G.green + '25' : s.type === 'down' ? G.red + '25' : G.goldBdr}`,
                borderLeft: `3px solid ${s.type === 'up' ? G.green : s.type === 'down' ? G.red : G.gold}`,
              }}>
                <span style={{ fontSize: 14, lineHeight: 1, marginTop: 1 }}>
                  {s.type === 'up' ? '↑' : s.type === 'down' ? '↓' : '→'}
                </span>
                <span style={{ fontFamily: G.mono, fontSize: 10, letterSpacing: '.04em', color: G.text, lineHeight: 1.6 }}>
                  {s.text}
                </span>
              </div>
            ))}
          </div>
          <p style={{ fontFamily: G.mono, fontSize: 8, color: 'rgba(26,25,22,0.25)', letterSpacing: '.06em', marginTop: 12, lineHeight: 1.5 }}>
            Analyse basée sur {allHistory.length} match{allHistory.length > 1 ? 's' : ''} · Métriques adaptées au poste {player.position}
          </p>
        </div>
      )}

      {/* Mini stats de synthèse */}
      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'repeat(2,1fr)' : 'repeat(4,1fr)', gap: 8 }}>
        <StatBox value={allHistory.length} label="Matchs analysés" color={G.gold} />
        <StatBox
          value={allHistory.filter(m => m.goals > 0).length}
          label="Matchs avec but"
          color={G.red}
        />
        <StatBox
          value={allHistory.length > 0 ? Math.round(allHistory.reduce((s, m) => s + (m.minutes || 0), 0) / allHistory.length) : '—'}
          label="Min. moy / match"
          color={G.blue}
        />
        <StatBox
          value={(() => {
            const rated = allHistory.filter(m => m.rating != null)
            if (!rated.length) return '—'
            return (rated.reduce((s, m) => s + m.rating, 0) / rated.length).toFixed(1)
          })()}
          label="Note moyenne"
          color={G.green}
        />
      </div>
    </div>
  )
}

/* ── Barre de stat (hero scouting, fond sombre) ── */
function ScoutBar({ label, display, pct, primary = false }) {
  const w = Math.round(Math.max(0, Math.min(pct || 0, 1)) * 100)
  return (
    <div style={{ marginBottom: 14 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
        <span style={{ fontFamily: 'monospace', fontSize: 9, letterSpacing: '.1em', textTransform: 'uppercase', color: 'rgba(26,25,22,0.5)' }}>{label}</span>
        <span style={{ fontFamily: 'monospace', fontSize: 10, fontWeight: 700, color: primary ? '#4F8EF7' : 'rgba(26,25,22,0.85)' }}>{display}</span>
      </div>
      <div style={{ height: 3, background: 'rgba(26,25,22,0.08)' }}>
        <div style={{ height: '100%', width: `${w}%`, background: primary ? '#4F8EF7' : 'rgba(26,25,22,0.35)', transition: 'width .8s ease' }} />
      </div>
    </div>
  )
}

/* ── Radar hexagonal (hero scouting) ── */
function ScoutRadar({ axes }) {
  const cx = 100, cy = 100, R = 70
  const n = axes.length
  const ang = (i) => (Math.PI * 2 * i) / n - Math.PI / 2
  const pt = (i, p) => ({ x: cx + R * p * Math.cos(ang(i)), y: cy + R * p * Math.sin(ang(i)) })
  const poly = (p) => axes.map((_, i) => { const q = pt(i, p); return `${q.x.toFixed(1)},${q.y.toFixed(1)}` }).join(' ')
  const dataPts = axes.map((a, i) => pt(i, Math.max(0, Math.min(1, a.pct || 0))))
  const dataPoly = dataPts.map(p => `${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(' ')
  return (
    <svg viewBox="0 0 200 200" style={{ width: 200, height: 200, maxWidth: '100%' }}>
      {[0.25, 0.5, 0.75, 1].map((l, i) => (
        <polygon key={`g${i}`} points={poly(l)} fill="none" stroke="rgba(26,25,22,0.08)" strokeWidth="1" />
      ))}
      {axes.map((_, i) => { const q = pt(i, 1); return (
        <line key={`a${i}`} x1={cx} y1={cy} x2={q.x} y2={q.y} stroke="rgba(26,25,22,0.06)" strokeWidth="1" />
      ) })}
      <polygon points={dataPoly} fill="rgba(79,142,247,0.15)" stroke="#4F8EF7" strokeWidth="1.5" />
      {dataPts.map((p, i) => <circle key={`p${i}`} cx={p.x} cy={p.y} r="3" fill="#4F8EF7" />)}
      {axes.map((a, i) => { const q = pt(i, 1.2); return (
        <text key={`l${i}`} x={q.x} y={q.y} textAnchor="middle" dominantBaseline="middle"
          fontSize="8" fontFamily="monospace" fill="rgba(26,25,22,0.5)">{a.label}</text>
      ) })}
    </svg>
  )
}

export default function PlayerProfile() {
  const { playerId } = useParams()
  const navigate = useNavigate()
  const { selectedSeason, isPastSeason } = useSeason()

  // ── État multi-saisons ──────────────────────────────────────
  // versions = chaîne de tous les player_ids (un par saison) liés via linked_player_id
  // displayedPlayerId = ID du joueur actuellement affiché (pas forcément celui de l'URL)
  const [versions, setVersions] = useState([])
  const [loadingVersions, setLoadingVersions] = useState(true)
  const [displayedPlayerId, setDisplayedPlayerId] = useState(playerId)

  const [player, setPlayer] = useState(null)
  const [stats, setStats] = useState(null)

  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [isFormOpen, setIsFormOpen] = useState(false)
  const [activeTab, setActiveTab] = useState('stats') // 'stats' | 'progression'
  const [statsView, setStatsView] = useState('official')
  const [isMobile, setIsMobile] = useState(() => typeof window !== 'undefined' && window.innerWidth < 768)

  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 768)
    window.addEventListener('resize', check)
    return () => window.removeEventListener('resize', check)
  }, [])

  // ── Au mount (ou changement d'URL) : charger l'historique multi-saisons ──
  useEffect(() => {
    setDisplayedPlayerId(playerId) // reset quand on change de joueur via l'URL
    loadVersions()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [playerId])

  // ── À chaque changement de displayedPlayerId : charger le joueur + ses stats ──
  useEffect(() => {
    if (!displayedPlayerId) return
    loadPlayer(displayedPlayerId)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [displayedPlayerId])

  const loadVersions = async () => {
    setLoadingVersions(true)
    try {
      const res = await fetch(`${API}/players/${playerId}/history`, {
        headers: authHeaders(),
      })
      if (!res.ok) {
        // Pas d'historique disponible : on ne casse pas le rendu, on continue avec une seule version
        setVersions([])
        return
      }
      const data = await res.json()
      const list = Array.isArray(data?.versions) ? data.versions : []
      // Tri décroissant par saison (plus récente d'abord)
      list.sort((a, b) => (b.season || '').localeCompare(a.season || ''))
      setVersions(list)
    } catch (e) {
      console.error('Erreur chargement historique:', e)
      setVersions([])
    } finally {
      setLoadingVersions(false)
    }
  }

  const loadPlayer = async (idToLoad) => {
    try {
      setLoading(true)
      const [data, statsData] = await Promise.all([
        playerService.getPlayer(idToLoad),
        playerService.getPlayerStats(idToLoad).catch(() => null),
      ])
      setPlayer(data)
      setStats(statsData)
    } catch (e) {
      setError('Joueur introuvable')
    } finally {
      setLoading(false)
    }
  }

  const handleEdit = async (data) => {
    await playerService.updatePlayer(displayedPlayerId, data)
    await loadPlayer(displayedPlayerId)
    setIsFormOpen(false)
  }

  if (loading) {
    return (
      <DashboardLayout>
        <div style={{ textAlign: 'center', padding: '120px 0' }}>
          <div style={{ width: 28, height: 28, border: `2px solid ${G.goldBdr}`, borderTopColor: G.gold, borderRadius: '50%', animation: 'spin .7s linear infinite', margin: '0 auto 14px' }} />
          <p style={{ fontFamily: G.mono, fontSize: 9, letterSpacing: '.14em', textTransform: 'uppercase', color: G.muted }}>Chargement...</p>
        </div>
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </DashboardLayout>
    )
  }

  if (error || !player) {
    return (
      <DashboardLayout>
        <div style={{ textAlign: 'center', padding: '120px 0' }}>
          <AlertCircle size={32} color={G.red} style={{ marginBottom: 16 }} />
          <h2 style={{ fontFamily: G.display, fontSize: 28, color: G.text, textTransform: 'uppercase', marginBottom: 8 }}>{error || 'Joueur introuvable'}</h2>
          <button onClick={() => navigate('/dashboard/players')} style={{ fontFamily: G.mono, fontSize: 10, letterSpacing: '.1em', textTransform: 'uppercase', color: G.gold, background: 'none', border: `1px solid ${G.goldBdr}`, padding: '10px 20px', cursor: 'pointer', marginTop: 12 }}>
            ← Retour à l'effectif
          </button>
        </div>
      </DashboardLayout>
    )
  }

  const foot = FOOT[player.preferred_foot]
  const status = STATUS[player.status] || STATUS['actif']
  const age = getAge(player.birth_date)

  const currentStats = stats?.[statsView] || stats?.all || null

  const matchStats = {
    played: currentStats?.matches_played ?? 0,
    starter: currentStats?.matches_starter ?? 0,
    sub: currentStats?.matches_sub ?? 0,
    minutes: currentStats?.total_minutes ?? 0,
    goals: currentStats?.goals ?? 0,
    assists: currentStats?.assists ?? 0,
  }

  const techStats = {
    passes: currentStats?.total_passes ?? null,
    passSuccess: currentStats?.avg_pass_success ?? null,
    shots: currentStats?.total_shots ?? null,
    shotsOnTarget: currentStats?.shots_on_target ?? null,
    duels: currentStats?.total_duels ?? null,
    duelsWon: currentStats?.duels_won ?? null,
    distance: currentStats?.total_distance_km ?? null,
    avgDistance: currentStats?.avg_distance_km ?? null,
    keyPasses: currentStats?.total_key_passes ?? null,
    tackles: currentStats?.total_tackles ?? null,
    interceptions: currentStats?.total_interceptions ?? null,
    saves: currentStats?.total_saves ?? null,
    yellowCards: currentStats?.yellow_cards ?? null,
  }

  const matchHistory = currentStats?.match_history ?? []

  const hasTechStats = Object.values(techStats).some(v => v !== null && v > 0)
  const hasMatchStats = matchStats.played > 0

  const officialCount = stats?.official?.matches_played ?? 0
  const friendlyCount = stats?.friendly?.matches_played ?? 0
  const allCount = stats?.all?.matches_played ?? 0

  const STATS_TABS = [
    { id: 'official', label: 'Officiels', count: officialCount, color: G.gold },
    { id: 'friendly', label: 'Amicaux', count: friendlyCount, color: G.blue },
    { id: 'all', label: 'Tout', count: allCount, color: G.muted },
  ].filter(t => t.count > 0 || t.id === 'official')

  return (
    <DashboardLayout>
      <style>{`
        ${globalStyles}
        @keyframes fadeIn { from { opacity:0; transform:translateY(8px); } to { opacity:1; transform:translateY(0); } }
        @keyframes spin { to { transform: rotate(360deg); } }
        @keyframes slideIn { from { opacity:0; transform:translateX(-12px); } to { opacity:1; transform:translateX(0); } }
      `}</style>

      {/* ── BACK BUTTON ── */}
      <button onClick={() => navigate('/dashboard/players')} style={{
        display: 'inline-flex', alignItems: 'center', gap: 8,
        fontFamily: G.mono, fontSize: 9, letterSpacing: '.12em', textTransform: 'uppercase',
        color: G.muted, background: 'none', border: 'none', cursor: 'pointer',
        padding: '0 0 20px', transition: 'color .15s',
      }}
        onMouseEnter={e => e.currentTarget.style.color = G.gold}
        onMouseLeave={e => e.currentTarget.style.color = G.muted}
      >
        <ArrowLeft size={14} /> Retour à l'effectif
      </button>

      {/* ── HERO SCOUTING REPORT ── */}
      {(() => {
        const titulaire = matchStats.played >= 5
        const duelsPct = techStats.duels > 0 ? (techStats.duelsWon / techStats.duels) * 100 : 0
        const passPct = techStats.passSuccess != null ? techStats.passSuccess : null
        const tackles = techStats.tackles ?? 0
        const intercs = techStats.interceptions ?? 0
        const shotsOT = techStats.shotsOnTarget ?? 0
        const shotsMax = techStats.shots > 0 ? techStats.shots : Math.max(shotsOT, 1)
        const avgDist = techStats.avgDistance ?? 0
        const abbr = POSITION_COLORS[player.position]?.abbr || ''
        const radarAxes = [
          { label: 'PASSES', pct: (passPct ?? 0) / 100 },
          { label: 'DUELS',  pct: duelsPct / 100 },
          { label: 'TACLES', pct: tackles / 50 },
          { label: 'INTERC', pct: intercs / 30 },
          { label: 'DIST',   pct: avgDist / 14 },
          { label: 'BUTS',   pct: (matchStats.goals ?? 0) / 20 },
        ]
        // Saison du joueur, sinon saison active du context. Si rien : on
        // n'affiche pas " · Saison" du tout (evite "SAISON —").
        const seasonLabel = formatSeasonShort(player.season) || formatSeasonShort(selectedSeason) || ''
        const avgCoachRating = currentStats?.avg_coach_rating ?? null
        const kpis = [
          { label: 'MATCHS',  value: matchStats.played, gold: true },
          { label: 'MINUTES', value: `${matchStats.minutes}'`, gold: false },
          { label: 'BUTS',    value: matchStats.goals, gold: false },
          { label: 'TACLES',  value: tackles, gold: false },
          { label: 'PASSES%', value: passPct != null ? `${Math.round(passPct)}%` : '—', gold: false },
          ...(avgCoachRating != null ? [{ label: 'NOTE MOY', value: `${avgCoachRating}/10`, gold: false }] : []),
        ]
        return (
        <div style={{
          border: '1px solid rgba(79,142,247,0.2)',
          marginBottom: 20, animation: 'fadeIn .4s ease', position: 'relative',
          overflow: 'hidden',
        }}>
          {/* Edit affordance — masqué en mode saison archivée */}
          {!isPastSeason && (
            <button onClick={() => setIsFormOpen(true)} aria-label="Modifier" style={{
              position: 'absolute', top: 16, right: 16, zIndex: 2,
              display: 'flex', alignItems: 'center', gap: 6,
              padding: '6px 14px', background: 'transparent',
              border: '1px solid rgba(79,142,247,0.4)',
              fontFamily: 'monospace', fontSize: 10, letterSpacing: '.1em',
              textTransform: 'uppercase', color: '#4F8EF7', cursor: 'pointer',
            }}>
              <Edit2 size={12} /> Modifier
            </button>
          )}

          {/* 1. Header identite — fond dark */}
          <div style={{ background: '#0d0c0a' }}>
          {isMobile ? (
            <div style={{
              borderBottom: '1px solid rgba(255,255,255,0.06)',
              padding: '0 20px 20px',
              paddingTop: !isPastSeason ? 44 : 16,
            }}>
              {/* Ligne 1 : photo + numéro/poste */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14 }}>
                {player.photo_url && (
                  <img
                    src={player.photo_url}
                    alt={player.name}
                    style={{
                      width: 48, height: 48,
                      objectFit: 'cover', borderRadius: '50%',
                      border: '2px solid rgba(79,142,247,0.35)', flexShrink: 0,
                    }}
                  />
                )}
                <div style={{
                  background: '#4F8EF7',
                  display: 'flex', flexDirection: 'column',
                  alignItems: 'center', justifyContent: 'center',
                  padding: '8px 14px', flexShrink: 0,
                }}>
                  <span style={{
                    fontFamily: 'monospace', fontSize: 30, fontWeight: 900, lineHeight: 1,
                    color: '#0d0c0a',
                  }}>
                    {player.number ?? '--'}
                  </span>
                  <span style={{
                    fontFamily: 'monospace', fontSize: 8, letterSpacing: '.1em',
                    textTransform: 'uppercase', marginTop: 3,
                    color: 'rgba(13,12,10,0.6)',
                  }}>
                    {abbr}
                  </span>
                </div>
              </div>
              {/* Ligne 2 : nom */}
              <div style={{ fontSize: 22, fontWeight: 900, color: '#f5f2eb', letterSpacing: '.03em', lineHeight: 1.05, marginBottom: 14, overflowWrap: 'break-word' }}>
                {player.name}
              </div>
              {/* Lignes 3-4 : badges */}
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                {player.category && (
                  <span style={{ fontFamily: 'monospace', fontSize: 9, letterSpacing: '.1em', textTransform: 'uppercase', padding: '3px 10px', border: '1px solid rgba(245,242,235,0.15)', color: 'rgba(245,242,235,0.5)' }}>
                    {player.category}
                  </span>
                )}
                {foot && (
                  <span style={{ fontFamily: 'monospace', fontSize: 9, letterSpacing: '.1em', textTransform: 'uppercase', padding: '3px 10px', border: `1px solid ${foot.color}40`, color: foot.color }}>
                    {foot.label}
                  </span>
                )}
                <span style={{ fontFamily: 'monospace', fontSize: 9, letterSpacing: '.1em', textTransform: 'uppercase', padding: '3px 10px', border: `1px solid ${player.status === 'actif' ? 'rgba(74,222,128,0.4)' : status.color + '40'}`, color: player.status === 'actif' ? '#4ade80' : status.color }}>
                  {player.status === 'actif' ? 'Actif' : status.label}
                </span>
                {age != null && (
                  <span style={{ fontFamily: 'monospace', fontSize: 9, letterSpacing: '.1em', textTransform: 'uppercase', padding: '3px 10px', border: '1px solid rgba(245,242,235,0.15)', color: 'rgba(245,242,235,0.5)' }}>
                    {age} ANS
                  </span>
                )}
                {player.height && (
                  <span style={{ fontFamily: 'monospace', fontSize: 9, letterSpacing: '.1em', textTransform: 'uppercase', padding: '3px 10px', border: '1px solid rgba(245,242,235,0.15)', color: 'rgba(245,242,235,0.5)' }}>
                    {player.height} cm
                  </span>
                )}
                {player.weight && (
                  <span style={{ fontFamily: 'monospace', fontSize: 9, letterSpacing: '.1em', textTransform: 'uppercase', padding: '3px 10px', border: '1px solid rgba(245,242,235,0.15)', color: 'rgba(245,242,235,0.5)' }}>
                    {player.weight} kg
                  </span>
                )}
              </div>
            </div>
          ) : (
            <div style={{
              display: 'flex', alignItems: 'stretch',
              borderBottom: '1px solid rgba(255,255,255,0.06)',
            }}>
              <div style={{
                width: 64, flexShrink: 0,
                background: titulaire ? '#4F8EF7' : 'rgba(245,242,235,0.06)',
                display: 'flex', flexDirection: 'column',
                alignItems: 'center', justifyContent: 'center', padding: '16px 0',
              }}>
                <span style={{
                  fontFamily: 'monospace', fontSize: 36, fontWeight: 900, lineHeight: 1,
                  color: titulaire ? '#0d0c0a' : 'rgba(245,242,235,0.3)',
                }}>
                  {player.number ?? '--'}
                </span>
                <span style={{
                  fontFamily: 'monospace', fontSize: 8, letterSpacing: '.1em',
                  textTransform: 'uppercase', marginTop: 4,
                  color: titulaire ? 'rgba(13,12,10,0.6)' : 'rgba(245,242,235,0.25)',
                }}>
                  {abbr}
                </span>
              </div>

              {player.photo_url && (
                <div style={{
                  width: 80, flexShrink: 0,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  padding: 12,
                  borderRight: '1px solid rgba(255,255,255,0.06)',
                }}>
                  <img
                    src={player.photo_url}
                    alt={player.name}
                    style={{
                      width: 56, height: 56,
                      objectFit: 'cover',
                      borderRadius: '50%',
                      border: '2px solid rgba(79,142,247,0.35)',
                    }}
                  />
                </div>
              )}

              <div style={{ flex: 1, minWidth: 0, padding: '16px 20px' }}>
                <div style={{ fontFamily: 'monospace', fontSize: 11, letterSpacing: '.14em', textTransform: 'uppercase', color: 'rgba(245,242,235,0.35)', marginBottom: 8 }}>
                  Fiche joueur{seasonLabel ? ` · Saison ${seasonLabel}` : ''}
                </div>
                <div style={{ fontSize: 26, fontWeight: 900, color: '#f5f2eb', letterSpacing: '.03em', lineHeight: 1.05, marginBottom: 12, overflowWrap: 'break-word' }}>
                  {player.name}
                </div>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  {player.category && (
                    <span style={{ fontFamily: 'monospace', fontSize: 9, letterSpacing: '.1em', textTransform: 'uppercase', padding: '3px 10px', border: '1px solid rgba(245,242,235,0.15)', color: 'rgba(245,242,235,0.5)' }}>
                      {player.category}
                    </span>
                  )}
                  {foot && (
                    <span style={{ fontFamily: 'monospace', fontSize: 9, letterSpacing: '.1em', textTransform: 'uppercase', padding: '3px 10px', border: `1px solid ${foot.color}40`, color: foot.color }}>
                      {foot.label}
                    </span>
                  )}
                  <span style={{ fontFamily: 'monospace', fontSize: 9, letterSpacing: '.1em', textTransform: 'uppercase', padding: '3px 10px', border: `1px solid ${player.status === 'actif' ? 'rgba(74,222,128,0.4)' : status.color + '40'}`, color: player.status === 'actif' ? '#4ade80' : status.color }}>
                    {player.status === 'actif' ? 'Actif' : status.label}
                  </span>
                </div>
              </div>

              <div style={{
                padding: '44px 20px 16px', textAlign: 'right',
                borderLeft: '1px solid rgba(255,255,255,0.06)',
                display: 'flex', flexDirection: 'column', gap: 10, justifyContent: 'center', flexShrink: 0,
              }}>
                {[
                  { label: 'Age', value: age != null ? `${age} ans` : '—' },
                  { label: 'Taille', value: player.height ? `${player.height} cm` : '—' },
                  { label: 'Poids', value: player.weight ? `${player.weight} kg` : '—' },
                ].map(d => (
                  <div key={d.label}>
                    <div style={{ fontFamily: 'monospace', fontSize: 8, letterSpacing: '.12em', textTransform: 'uppercase', color: 'rgba(245,242,235,0.35)' }}>{d.label}</div>
                    <div style={{ fontFamily: 'monospace', fontSize: 11, fontWeight: 700, color: '#f5f2eb' }}>{d.value}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
          </div>

          {/* 2+3+4 — fond clair */}
          <div style={{ background: '#ffffff' }}>

          {/* 2. Bande KPIs */}
          <div style={{ display: 'grid', gridTemplateColumns: `repeat(${kpis.length}, 1fr)`, borderBottom: '1px solid rgba(26,25,22,0.07)' }}>
            {kpis.map((k, i) => (
              <div key={k.label} style={{
                padding: '16px 8px', textAlign: 'center',
                borderRight: i < kpis.length - 1 ? '1px solid rgba(26,25,22,0.07)' : 'none',
              }}>
                <div style={{ fontFamily: 'monospace', fontSize: 24, fontWeight: 900, lineHeight: 1, color: k.gold ? '#4F8EF7' : 'rgba(26,25,22,0.85)' }}>
                  {k.value}
                </div>
                <div style={{ fontFamily: 'monospace', fontSize: 8, letterSpacing: '.1em', textTransform: 'uppercase', color: 'rgba(26,25,22,0.4)', marginTop: 6 }}>
                  {k.label}
                </div>
              </div>
            ))}
          </div>

          {/* 3. Corps : performance + radar */}
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 0 }}>
            <div style={{ padding: 20, borderRight: isMobile ? 'none' : '1px solid rgba(26,25,22,0.07)', borderBottom: isMobile ? '1px solid rgba(26,25,22,0.07)' : 'none' }}>
              <div style={{ fontFamily: 'monospace', fontSize: 9, letterSpacing: '.16em', textTransform: 'uppercase', color: '#4F8EF7', marginBottom: 18 }}>
                Performance saison
              </div>
              <ScoutBar label="Passes reussies" primary display={passPct != null ? `${Math.round(passPct)}%` : '—'} pct={(passPct ?? 0) / 100} />
              <ScoutBar label="Duels gagnes" display={techStats.duels > 0 ? `${Math.round(duelsPct)}%` : '—'} pct={duelsPct / 100} />
              <ScoutBar label="Interceptions" display={intercs || '—'} pct={intercs / 30} />
              {/* PROMESSES-ANALYSE-001 · barre « Tirs cadres » retiree ·
                  `shots_on_target` ne vient que du chemin pipeline, et
                  `player_stats` est nul sur les 70 matchs de la base.
                  ⚠️ Les QUATRE autres barres de ce bloc viennent de la meme
                  source et affichent « — » en permanence · passes reussies,
                  duels gagnes, interceptions, distance. Les retirer viderait
                  « Performance saison » entierement, ce qui est une decision
                  produit · voir P1-METRIQUES-JOUEUR-VIDES-001. */}
              <ScoutBar label="Distance moy / match" display={avgDist ? `${avgDist} km` : '—'} pct={avgDist / 14} />
            </div>
            <div style={{ padding: 20, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <div style={{ alignSelf: 'flex-start', fontFamily: 'monospace', fontSize: 9, letterSpacing: '.16em', textTransform: 'uppercase', color: '#4F8EF7', marginBottom: 12 }}>
                Profil radar
              </div>
              <ScoutRadar axes={radarAxes} />
            </div>
          </div>

          {/* 4. Footer distance totale */}
          <div style={{
            borderTop: '1px solid rgba(26,25,22,0.07)', padding: '12px 20px',
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          }}>
            <span style={{ fontFamily: 'monospace', fontSize: 9, letterSpacing: '.14em', textTransform: 'uppercase', color: 'rgba(26,25,22,0.4)' }}>
              Distance totale parcourue
            </span>
            <span style={{ fontFamily: 'monospace', fontSize: 20, fontWeight: 900, color: '#4F8EF7' }}>
              {techStats.distance != null && techStats.distance > 0 ? `${techStats.distance} km` : '—'}
            </span>
          </div>
          </div>
        </div>
        )
      })()}

      {/* ── SÉLECTEUR SAISON LOCAL (multi-saisons) ── */}
      {versions.length > 1 && (
        <div style={{ marginBottom: 16, animation: 'fadeIn .4s ease .03s both' }}>
          <div style={{ fontFamily: G.mono, fontSize: 8, letterSpacing: '.16em', textTransform: 'uppercase', color: G.muted, marginBottom: 8, display: 'flex', alignItems: 'center', gap: 8 }}>
            <Calendar size={10} color={G.muted} />
            Historique du joueur
          </div>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {versions.map(v => {
              const isActive = v.id === displayedPlayerId
              return (
                <button
                  key={v.id}
                  onClick={() => setDisplayedPlayerId(v.id)}
                  style={{
                    padding: '8px 14px',
                    background: isActive ? G.gold : 'transparent',
                    color: isActive ? G.dark : G.text,
                    border: `1px solid ${isActive ? G.gold : G.border}`,
                    fontFamily: G.mono, fontSize: 10, letterSpacing: '.08em',
                    cursor: isActive ? 'default' : 'pointer',
                    transition: 'all .15s',
                    display: 'flex', alignItems: 'center', gap: 8,
                  }}>
                  <span style={{ fontWeight: isActive ? 700 : 400 }}>
                    {formatSeasonShort(v.season)}
                  </span>
                  {v.category && (
                    <span style={{
                      fontFamily: G.mono, fontSize: 8, letterSpacing: '.08em',
                      padding: '1px 6px',
                      background: isActive ? 'rgba(10,9,8,0.15)' : G.goldBg,
                      color: isActive ? G.dark : G.gold,
                      border: `1px solid ${isActive ? 'transparent' : G.goldBdr}`,
                    }}>
                      {v.category}
                    </span>
                  )}
                </button>
              )
            })}
          </div>
        </div>
      )}

      {/* ── BANDEAU SAISON DIFFÉRENTE DE LA SIDEBAR ── */}
      {player?.season && player.season !== selectedSeason && (
        <div style={{
          marginBottom: 16,
          padding: '10px 16px',
          background: G.goldBg,
          border: `1px solid ${G.goldBdr}`,
          borderLeft: `3px solid ${G.gold}`,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          gap: 12, flexWrap: 'wrap',
          animation: 'fadeIn .4s ease .04s both',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <Calendar size={14} color={G.gold} />
            <span style={{ fontFamily: G.mono, fontSize: 10, letterSpacing: '.04em', color: G.text, lineHeight: 1.5 }}>
              Tu consultes la <strong>saison {formatSeasonShort(player.season)}</strong>{' '}
              · ta sélection actuelle est <strong>{formatSeasonShort(selectedSeason)}</strong>
            </span>
          </div>
          {(() => {
            // Chercher la version qui correspond à selectedSeason de la sidebar
            const matchingVersion = versions.find(v => v.season === selectedSeason)
            if (!matchingVersion || matchingVersion.id === displayedPlayerId) return null
            return (
              <button
                onClick={() => setDisplayedPlayerId(matchingVersion.id)}
                style={{
                  fontFamily: G.mono, fontSize: 9, letterSpacing: '.1em', textTransform: 'uppercase',
                  color: G.gold, background: 'transparent', border: `1px solid ${G.gold}`,
                  padding: '6px 12px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6,
                  fontWeight: 700,
                }}>
                Voir {formatSeasonShort(selectedSeason)} <ChevronRight size={11} />
              </button>
            )
          })()}
        </div>
      )}

      {/* ── TABS PRINCIPAUX : Stats / Progression ── */}
      <div style={{ display: 'flex', gap: 0, marginBottom: 20, borderBottom: `2px solid ${G.border}`, animation: 'fadeIn .4s ease .05s both' }}>
        {[
          { id: 'stats', label: 'Statistiques' },
          { id: 'progression', label: 'Progression', badge: allCount > 0 ? `${allCount}` : null },
        ].map(tab => (
          <button key={tab.id} onClick={() => setActiveTab(tab.id)} style={{
            padding: isMobile ? '10px 16px' : '12px 24px',
            fontFamily: G.mono, fontSize: 9, letterSpacing: '.12em', textTransform: 'uppercase',
            background: 'transparent', border: 'none',
            borderBottom: activeTab === tab.id ? `2px solid ${G.gold}` : '2px solid transparent',
            marginBottom: -2,
            color: activeTab === tab.id ? G.gold : G.muted,
            cursor: 'pointer', transition: 'all .15s',
            display: 'flex', alignItems: 'center', gap: 8,
          }}>
            {tab.label}
            {tab.badge && (
              <span style={{
                fontFamily: G.mono, fontSize: 8, padding: '1px 7px',
                background: activeTab === tab.id ? G.gold + '18' : 'transparent',
                color: activeTab === tab.id ? G.gold : G.muted,
                border: `1px solid ${activeTab === tab.id ? G.goldBdr : G.border}`,
              }}>{tab.badge}</span>
            )}
          </button>
        ))}
      </div>

      {/* ══ TAB : STATISTIQUES ══ */}
      {activeTab === 'stats' && (
        <>
          {/* Sous-tabs type de match */}
          {allCount > 0 && (
            <div style={{ display: 'flex', gap: 0, marginBottom: 20, borderBottom: `1px solid ${G.border}`, overflowX: 'auto', animation: 'fadeIn .4s ease .05s both' }}>
              {STATS_TABS.map(tab => (
                <button key={tab.id} onClick={() => setStatsView(tab.id)} style={{
                  padding: '10px 18px', fontFamily: G.mono, fontSize: 9, letterSpacing: '.1em', textTransform: 'uppercase',
                  background: 'transparent', border: 'none',
                  borderBottom: statsView === tab.id ? `2px solid ${tab.color}` : '2px solid transparent',
                  color: statsView === tab.id ? tab.color : G.muted,
                  cursor: 'pointer', transition: 'all .15s', whiteSpace: 'nowrap',
                  display: 'flex', alignItems: 'center', gap: 6,
                }}>
                  {tab.label}
                  <span style={{
                    fontFamily: G.mono, fontSize: 8, padding: '1px 6px',
                    background: statsView === tab.id ? tab.color + '18' : 'transparent',
                    color: statsView === tab.id ? tab.color : G.muted,
                    borderRadius: 2,
                  }}>{tab.count}</span>
                </button>
              ))}
            </div>
          )}


          {/* ── STATS TECHNIQUES ── */}
          {hasTechStats && (
            <div style={{ background: '#0d0c0a', border: '1px solid rgba(79,142,247,0.15)', padding: '24px', marginBottom: 20, animation: 'fadeIn .4s ease .3s both' }}>
              <div style={{ fontFamily: G.mono, fontSize: 9, letterSpacing: '.16em', textTransform: 'uppercase', color: G.gold, marginBottom: 20, display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ width: 16, height: 1, background: G.gold }} />Performance technique
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(4, 1fr)', gap: 8, marginBottom: 24 }}>
                {techStats.keyPasses > 0 && <StatBox dark value={techStats.keyPasses} label="Passes clés" color={G.gold} />}
                {techStats.tackles > 0 && <StatBox dark value={techStats.tackles} label="Tacles" color={G.blue} />}
                {techStats.interceptions > 0 && <StatBox dark value={techStats.interceptions} label="Interceptions" color={G.green} />}
                {techStats.saves > 0 && <StatBox dark value={techStats.saves} label="Arrêts" color={G.orange} />}
                {techStats.yellowCards > 0 && <StatBox dark value={techStats.yellowCards} label="Cartons J." color={G.yellow} />}
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 28 }}>
                <div>
                  {techStats.passes !== null && techStats.passes > 0 && (
                    <ProgressBar dark label="Passes totales" value={techStats.passes} max={techStats.passes} color={G.gold} />
                  )}
                  {techStats.passSuccess !== null && (
                    <ProgressBar dark label="% Passes réussies (moy.)" value={techStats.passSuccess} max={100} color={G.green} />
                  )}
                  {techStats.avgDistance !== null && (
                    <ProgressBar dark label={`Distance moy. / match (${techStats.avgDistance} km)`} value={techStats.avgDistance} max={14} color={G.blue} />
                  )}
                </div>
                <div>
                  {techStats.shots !== null && techStats.shots > 0 && (
                    <ProgressBar dark label="Tirs totaux" value={techStats.shots} max={techStats.shots} color={G.red} />
                  )}
                  {/* PROMESSES-ANALYSE-001 · barre « Tirs cadrés » retiree ·
                      sa garde `techStats.shots > 0` n'a jamais ete vraie. */}
                  {techStats.duelsWon !== null && techStats.duels > 0 && (
                    <ProgressBar dark label={`Duels gagnés (${techStats.duelsWon}/${techStats.duels})`} value={techStats.duelsWon} max={techStats.duels} color={G.gold} />
                  )}
                </div>
              </div>
            </div>
          )}

          {/* ── HISTORIQUE MATCHS ── */}
          {matchHistory.length > 0 && (
            <div style={{ background: G.card, border: `1px solid ${G.border}`, padding: '24px', marginBottom: 20, animation: 'fadeIn .4s ease .4s both' }}>
              <div style={{ fontFamily: G.mono, fontSize: 9, letterSpacing: '.16em', textTransform: 'uppercase', color: G.gold, marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ width: 16, height: 1, background: G.gold }} />Historique matchs
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 1, background: G.border }}>
                {matchHistory.map((m, i) => {
                  // Oriente club via matchResult (deja juste avant, mais la
                  // convention ne doit vivre qu'a un endroit).
                  const letter = resultLetter(m)
                  const resultColor = letter === 'V' ? G.green : letter === 'N' ? G.orange : G.red
                  const resultLabel = letter
                  const score = scoreDisplay(m, ' - ')
                  const dateStr = m.date ? new Date(m.date).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' }) : ''
                  return (
                    <div key={m.match_id} style={{
                      background: G.card, padding: isMobile ? '12px' : '12px 16px',
                      display: 'flex', alignItems: 'center', gap: isMobile ? 10 : 16,
                      cursor: 'pointer', transition: 'background .1s',
                    }}
                      onClick={() => navigate(`/dashboard/matches/${m.match_id}`)}
                      onMouseEnter={e => e.currentTarget.style.background = G.goldBg}
                      onMouseLeave={e => e.currentTarget.style.background = G.card}
                    >
                      <div style={{
                        width: 28, height: 28, background: resultColor + '15', border: `1px solid ${resultColor}30`,
                        display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                        fontFamily: G.mono, fontSize: 11, fontWeight: 700, color: resultColor,
                      }}>{resultLabel}</div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontFamily: G.display, fontSize: 14, color: G.text, textTransform: 'uppercase', letterSpacing: '.02em', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {m.is_home ? 'vs' : '@'} {m.opponent}
                        </div>
                        <div style={{ fontFamily: G.mono, fontSize: 8, color: G.muted, letterSpacing: '.08em' }}>
                          {dateStr} {m.competition ? `· ${m.competition}` : ''}
                        </div>
                      </div>
                      <div style={{ fontFamily: G.display, fontSize: 18, color: G.text, flexShrink: 0 }}>{score}</div>
                      <div style={{ display: 'flex', gap: isMobile ? 6 : 12, alignItems: 'center', flexShrink: 0 }}>
                        <div style={{ textAlign: 'center' }}>
                          <div style={{ fontFamily: G.display, fontSize: 14, color: G.text }}>{m.minutes}'</div>
                          <div style={{ fontFamily: G.mono, fontSize: 7, color: G.muted, letterSpacing: '.08em', textTransform: 'uppercase' }}>min</div>
                        </div>
                        {m.goals > 0 && (
                          <div style={{ textAlign: 'center' }}>
                            <div style={{ fontFamily: G.display, fontSize: 14, color: G.gold }}>{m.goals}</div>
                            <div style={{ fontFamily: G.mono, fontSize: 7, color: G.muted, letterSpacing: '.08em', textTransform: 'uppercase' }}>but{m.goals > 1 ? 's' : ''}</div>
                          </div>
                        )}
                        {m.assists > 0 && (
                          <div style={{ textAlign: 'center' }}>
                            <div style={{ fontFamily: G.display, fontSize: 14, color: G.blue }}>{m.assists}</div>
                            <div style={{ fontFamily: G.mono, fontSize: 7, color: G.muted, letterSpacing: '.08em', textTransform: 'uppercase' }}>pd</div>
                          </div>
                        )}
                        <span style={{
                          fontFamily: G.mono, fontSize: 7, letterSpacing: '.08em', textTransform: 'uppercase',
                          padding: '2px 6px', background: m.starter ? G.green + '12' : 'rgba(26,25,22,0.05)',
                          border: `1px solid ${m.starter ? G.green + '30' : G.border}`,
                          color: m.starter ? G.green : G.muted,
                        }}>
                          {m.starter ? 'TIT' : 'RMP'}
                        </span>
                      </div>
                    </div>
                  )
                })}
              </div>
            </div>
          )}

          {/* Placeholder si pas de matchs */}
          {!hasMatchStats && (
            <div style={{
              background: G.card, border: `1px dashed ${G.border}`,
              padding: '48px 24px', textAlign: 'center', marginBottom: 28,
              animation: 'fadeIn .4s ease .3s both',
            }}>
              <TrendingUp size={28} color='rgba(26,25,22,0.15)' style={{ marginBottom: 12 }} />
              <h3 style={{ fontFamily: G.display, fontSize: 22, textTransform: 'uppercase', color: G.muted, marginBottom: 6, letterSpacing: '.02em' }}>
                Évolution & performance
              </h3>
              <p style={{ fontFamily: G.mono, fontSize: 10, letterSpacing: '.06em', color: 'rgba(26,25,22,0.30)', lineHeight: 1.7, maxWidth: 360, margin: '0 auto' }}>
                Les graphiques de progression et les stats collectives apparaîtront après les premiers matchs saisis.
              </p>
            </div>
          )}

        </>
      )}

      {/* ══ TAB : PROGRESSION ══ */}
      {activeTab === 'progression' && (
        <div style={{ animation: 'fadeIn .4s ease' }}>
          <ProgressionTab stats={stats} player={player} isMobile={isMobile} />
        </div>
      )}

      {/* ── EDIT FORM MODAL ── */}
      <PlayerForm
        isOpen={isFormOpen}
        onClose={() => setIsFormOpen(false)}
        onSubmit={handleEdit}
        player={player}
      />
    </DashboardLayout>
  )
}
